import React from "react";
import Layout from "@/components/utu/Layout";
import { GlassCard, GoldPillButton } from "@/components/utu/atoms";
import { SEO } from "@/components/SEO";
import { TECH_STACK, BOOKING_URL } from "@/mock";
import { Compass, Shield, Award, Users, ArrowRight } from "lucide-react";

export default function About() {
  return (
    <Layout>
      <SEO
        title="About the AI Lab & Tech Stack"
        description="Learn about UTU Africa's lab philosophy, our team, and the technical rationale for choosing Django, FastAPI, React, and local MCP servers for data sovereignty and security."
        url="/about"
      />
      <section className="mx-auto max-w-5xl px-5 py-12 md:px-8">
        {/* Header Hero */}
        <div className="mb-16 text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 rounded-full border border-amber-500/20 bg-amber-500/5 px-3 py-1 text-xs text-[hsl(var(--utu-gold))] backdrop-blur-xl mb-4">
            <Compass className="h-4 w-4" />
            Our Philosophy
          </div>
          <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl md:text-6xl mb-6">
            The AI & Tech Lab
          </h1>
          <p className="text-lg text-white/70 leading-relaxed">
            We build tools that ground computational intelligence in linguistic and structural context. We are an engineering lab designed to solve the hardest data sovereignty and localization challenges.
          </p>
        </div>

        {/* Philosophy Core Grid */}
        <div className="grid gap-6 md:grid-cols-3 mb-16">
          <GlassCard className="p-6 border-white/10">
            <Shield className="h-6 w-6 text-[hsl(var(--utu-gold))] mb-4" />
            <h4 className="text-lg font-bold text-white mb-2">Data Sovereignty</h4>
            <p className="text-xs text-white/60 leading-relaxed">
              We believe organizations must control the data that shapes their intelligence. We design architectures that keep data inside your VPN, air-gapped from commercial AI vendors.
            </p>
          </GlassCard>

          <GlassCard className="p-6 border-white/10">
            <Award className="h-6 w-6 text-[hsl(var(--utu-gold))] mb-4" />
            <h4 className="text-lg font-bold text-white mb-2">Linguistic Integrity</h4>
            <p className="text-xs text-white/60 leading-relaxed">
              Sub-word vocabularies should represent native morphologies. We actively retrain tokenizers to ensure Low-Resource languages are not marginalized by cost or accuracy gaps.
            </p>
          </GlassCard>

          <GlassCard className="p-6 border-white/10">
            <Users className="h-6 w-6 text-[hsl(var(--utu-gold))] mb-4" />
            <h4 className="text-lg font-bold text-white mb-2">Systems over Hype</h4>
            <p className="text-xs text-white/60 leading-relaxed">
              We focus on building functional systems that solve client bottlenecks, replacing speculative AI projections with robust database transactions and clear metrics.
            </p>
          </GlassCard>
        </div>

        {/* Stack Rationale Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-4 text-center md:text-left">Tech Stack & Rationale</h2>
          <p className="text-sm text-white/60 max-w-2xl mb-8">
            Our systems are built on open-source, industry-standard stacks to guarantee data custody, performance limits, and platform independence.
          </p>

          <div className="grid gap-4 sm:grid-cols-2">
            {TECH_STACK.map((tech) => (
              <GlassCard key={tech.name} className="p-6">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="text-lg font-bold text-white">{tech.name}</h4>
                  <span className="text-[10px] text-white/50 bg-white/5 border border-white/10 px-2 py-0.5 rounded font-mono uppercase">
                    {tech.category}
                  </span>
                </div>
                <p className="text-xs text-white/60 leading-relaxed">{tech.reason}</p>
              </GlassCard>
            ))}
          </div>
        </div>

        {/* Core Rationale Summary */}
        <GlassCard className="p-8 md:p-12 mb-16 bg-[radial-gradient(circle_at_20%_0%,rgba(252,211,77,0.05),transparent_40%)]">
          <h3 className="text-2xl font-bold text-white mb-4">Sovereign & Compliant</h3>
          <p className="text-sm text-white/70 leading-relaxed mb-4">
            We explicitly advocate for **FastAPI** and **Django** on the backend to achieve a balance between asynchronous micro-routing performance and transactional database safety (ACID compliance via PostgreSQL). 
          </p>
          <p className="text-sm text-white/70 leading-relaxed">
            By avoiding proprietary hosting lock-in, we use **Docker** and **Kubernetes** to deploy your infrastructure to any hardware provider—including AWS, Azure, Google Cloud, or local bare-metal configurations.
          </p>
        </GlassCard>

        {/* Call to Action */}
        <div className="text-center">
          <h3 className="text-2xl font-bold text-white mb-4">Ready to Partner With Us?</h3>
          <p className="text-sm text-white/60 mb-8 max-w-xl mx-auto">
            Discuss your system needs with our engineering team and learn how we deploy custom, self-hosted technology stacks.
          </p>
          <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
            <GoldPillButton className="h-12 px-8">
              Consult with Our Engineers
              <ArrowRight className="h-4 w-4 ml-2" />
            </GoldPillButton>
          </a>
        </div>
      </section>
    </Layout>
  );
}
